/**
 * **工作区** —— 内置预设 + 用户自存的布局，合成一份可切换的清单。
 *
 * 这一层对应 Blender 顶栏那排 `Layout / Modeling / Sculpting` 标签：
 * 点一下换**整套**窗口排布。
 *
 * ## 和 `presets.ts` 的分工
 *
 *   `presets.ts`     内置的 7 份排布，**代码里写死的**（改不了、删不掉）
 *   `workspaces.ts`  上面那 7 份 + 用户自己存的，统一成同一种形状
 *
 * 分开是因为它们的**生命周期完全不同**：内置的跟着代码走，
 * 自存的要落盘、会被删、可能是旧版本存的。
 *
 * ## ⚠️ 结构指纹用于「当前在哪一个工作区」
 *
 * 判定只看**结构**（Area 数量/顺序/编辑器/切分方向），**不看比例**。
 * 理由：
 *   · 拖一下分隔条就掉出「当前工作区」是很糟的体验 ——
 *     用户会以为自己不小心切走了；
 *   · 和 Blender 一致：在工作区里调分割不会「离开」这个工作区。
 *
 * 匹配时**先查用户自存的、再查内置的** —— 于是用户存了一份
 * 「节点图 + 输出（比例调过）」，打勾的是他自己那个名字，
 * 而不是被内置的同名结构抢走。
 */
import { deserializeLayout, serializeLayout } from './areas'
import type { LayoutState } from './areas'
import { LAYOUT_PRESETS, describeLayout } from './presets'

/** 落盘的形状。⚠️ 存的是 `LayoutDoc`（可序列化），不是 `LayoutState` */
export interface SavedWorkspace {
  id: string
  label: string
  layout: ReturnType<typeof serializeLayout>
}

/** 标签栏里的一项 —— 内置和自存在这里长得一样 */
export interface WorkspaceEntry {
  id: string
  label: string
  /** 一句话说明（内置的有，自存的没有） */
  hint?: string
  /** 自存的可以删；内置的不行 */
  removable: boolean
  build: () => LayoutState
}

const STORAGE_KEY = 'node-print.workspaces.v1'

/**
 * 从 localStorage 读用户自存的工作区。
 *
 * ⚠️ **任何一条坏了都只丢那一条**，不能让整份清单消失：
 * 这些是用户自己排好的东西，读不出来比读错更让人恼火。
 * 所以逐条 `try`，并且过了 `deserializeLayout`（它自己不抛，
 * 会在结构非法时退回默认布局 —— 这里再加一道「id / label 必须是字符串」）。
 */
export function loadSavedWorkspaces(storage: Pick<Storage, 'getItem'> | null): SavedWorkspace[] {
  if (!storage) return []
  let raw: unknown
  try {
    raw = JSON.parse(storage.getItem(STORAGE_KEY) ?? '[]')
  } catch {
    return []
  }
  if (!Array.isArray(raw)) return []

  const out: SavedWorkspace[] = []
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue
    const item = entry as { id?: unknown; label?: unknown; layout?: unknown }
    if (typeof item.id !== 'string' || !item.id) continue
    if (typeof item.label !== 'string' || !item.label) continue
    if (!item.layout || typeof item.layout !== 'object') continue
    out.push({ id: item.id, label: item.label, layout: item.layout as SavedWorkspace['layout'] })
  }
  return out
}

/** 写回去。配额满之类的失败**静默吞掉** —— 存不上不该让整个界面报错 */
export function persistSavedWorkspaces(
  storage: Pick<Storage, 'setItem'> | null,
  list: readonly SavedWorkspace[],
): void {
  if (!storage) return
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(list))
  } catch {
    /* 忽略：本地存储满了 / 隐私模式禁用 —— 都不该影响正在做的事 */
  }
}

/** 用户自存 → 标签项 */
export function savedToEntry(item: SavedWorkspace): WorkspaceEntry {
  return {
    id: item.id,
    label: item.label,
    removable: true,
    /*
     * ⚠️ 每次 `build()` 都重新反序列化一份**新的**树。
     *
     * 不能缓存同一棵树反复给：`LayoutState` 是可变对象，
     * `NpAreaTree` 直接操作它。同一个引用被两次套用的话，
     * 上一次的改动（比如关了一个 Area）会留在里面。
     */
    build: () => deserializeLayout(item.layout),
  }
}

/** 内置预设 → 标签项 */
export function presetToEntry(preset: (typeof LAYOUT_PRESETS)[number]): WorkspaceEntry {
  return {
    id: preset.id,
    label: preset.label,
    hint: preset.hint,
    removable: false,
    build: preset.build,
  }
}

/**
 * 完整清单：**自存的在前**（匹配时优先命中）。
 *
 * 顺序也决定了标签栏里的排列 —— 用户存的东西排在内置前面，
 * 因为它们是他刚做的、更可能是他想要的。
 */
export function buildWorkspaceList(saved: readonly SavedWorkspace[]): WorkspaceEntry[] {
  return [...saved.map(savedToEntry), ...LAYOUT_PRESETS.map(presetToEntry)]
}

/**
 * 「当前这棵树属于哪个工作区」。
 *
 * 只看结构（见文件头注释）。都不匹配时返回 `null` ——
 * 此时标签栏会显示一个「自定义」，而不是硬把勾打在某一项上。
 */
export function matchWorkspaceId(
  layout: LayoutState,
  list: readonly WorkspaceEntry[],
): string | null {
  const signature = describeLayout(layout)
  for (const entry of list) {
    if (describeLayout(entry.build()) === signature) return entry.id
  }
  return null
}

/** 生成一个不会和现有条目撞车的 id */
export function nextWorkspaceId(existing: readonly { id: string }[]): string {
  const taken = new Set(existing.map((item) => item.id))
  for (let i = 1; i < 1000; i += 1) {
    const id = `user-${i}`
    if (!taken.has(id)) return id
  }
  return `user-${Date.now()}`
}
